import * as React from 'react';

import { CurrencyCode, Money } from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  DataGrid,
  DataGridSelection,
  FloatingToolbar,
  ListScreenLayout,
  Pagination,
  SearchField,
  type PaginationProps,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';
import { LucideX } from 'lucide-react';

import * as state from '../../_state/financeBalance.state';
import { FinanceRootLayout } from '../_layouts/FinanceRootLayout';

import { useBalancesListColumns } from './hooks/useBalancesListColumns';
import { NetPayDialog } from './NetPayDialog';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { SelectCurrency } from '~/shared/components/select-fields/SelectCurrency';
import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

type AccountBalance = {
  id: string;
  name: string;
  balances: Money[];
  pendingTransactions: number;
  actions?: never;
};

//
// ⚛️ ⚛️ ⚛️ BALANCES LIST VIEW ⚛️ ⚛️ ⚛️
//
export const BalancesListView = () => {
  const t = useTranslations();

  // Atoms
  const { error: balancesError, isLoading } = useAtomValue(
    state.$remoteBalances,
  );
  const sortedAndFilteredAccounts = useAtomValue(
    state.$sortedAndFilteredAccounts,
  );
  const paginatedAccounts = useAtomValue(state.$paginatedAccounts);

  const [searchTerm, setSearchTerm] = useDebouncedAtom(state.$searchTerm, 500);
  const [currencyFilter, setCurrencyFilter] = useAtom(state.$currency);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [selection, setSelection] = React.useState<DataGridSelection>(
    new Set(),
  );
  const [isNetPayDialogOpen, setIsNetPayDialogOpen] = React.useState(false);
  const [selectedAccountForNetPay, setSelectedAccountForNetPay] =
    React.useState<{ id: string; name: string } | null>(null);

  const handleNetPay = React.useCallback((id: string, name: string) => {
    setSelectedAccountForNetPay({ id, name });
    setIsNetPayDialogOpen(true);
  }, []);

  const { columns } = useBalancesListColumns({ onNetPay: handleNetPay });

  const total = sortedAndFilteredAccounts.length;

  // Memoized values
  const selectedRowsCount = React.useMemo(
    () => (selection === 'all' ? paginatedAccounts.length : selection.size),
    [selection, paginatedAccounts],
  );

  // Callbacks
  const handlePaginationChange = React.useCallback<
    NonNullable<PaginationProps['onChange']>
  >(
    ({ limit: newLimit, offset: newOffset }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleSearch = React.useCallback(
    (value: string) => {
      setSearchTerm(value);
      setOffset(0);
    },
    [setSearchTerm, setOffset],
  );

  return (
    <FinanceRootLayout className="@BalancesListView">
      <ListScreenLayout
        filters={
          <>
            <SearchField
              autoFocus
              aria-label={t.balances.list.search.label}
              className="min-h-0 w-80"
              placeholder={t.balances.list.search.placeholder}
              value={searchTerm}
              onChange={handleSearch}
            />
            <SelectCurrency
              className="w-44"
              showAllCurrenciesOption
              value={currencyFilter}
              onChange={(value) =>
                setCurrencyFilter(
                  value === 'all' ? undefined : (value as CurrencyCode),
                )
              }
            />
          </>
        }
        footer={
          total > limit && (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          )
        }
        className="@BalancesListView"
      >
        <DataGrid<AccountBalance>
          selectionMode="multiple"
          columns={columns}
          dataSource={paginatedAccounts}
          rowKey="id"
          aria-label="List of Balances"
          fullWidth
          isLoading={isLoading}
          hasError={!!balancesError}
          errorPanelProps={
            balancesError
              ? {
                  description: t.balances.list.error(balancesError.message),
                  centered: true,
                }
              : undefined
          }
          selectedKeys={selection}
          onSelectionChange={setSelection}
          emptyPanelProps={{
            title: t.balances.list.empty.title,
            description: t.balances.list.empty.description,
          }}
        />
      </ListScreenLayout>

      <FloatingToolbar visible={selectedRowsCount > 0}>
        <div className="flex flex-row items-center justify-between gap-3">
          <div className="flex flex-row items-center gap-2 text-sm">
            <Badge color="primary" size="small">
              {selectedRowsCount}
            </Badge>
            {t.balances.list.selected(selectedRowsCount)}
          </div>
          <div className="flex flex-row items-center gap-2">
            {selectedRowsCount === 1 && (
              <Button
                variant="solid"
                color="primary"
                size="small"
                onPress={() => {
                  const selectedId =
                    selection === 'all'
                      ? paginatedAccounts[0]?.id
                      : [...selection][0];
                  const account = paginatedAccounts.find(
                    (a) => a.id === selectedId,
                  );
                  if (account) {
                    handleNetPay(account.id, account.name);
                  }
                }}
              >
                {t.balances.netPay.title}
              </Button>
            )}
            <LucideX
              className="size-4 cursor-pointer text-neutral/80"
              onClick={() => setSelection(new Set())}
            />
          </div>
        </div>
      </FloatingToolbar>

      {selectedAccountForNetPay && (
        <NetPayDialog
          isOpen={isNetPayDialogOpen}
          onClose={() => {
            setIsNetPayDialogOpen(false);
            setSelectedAccountForNetPay(null);
          }}
          accountId={selectedAccountForNetPay.id}
          accountName={selectedAccountForNetPay.name}
          onSuccess={() => {
            setSelection(new Set());
          }}
        />
      )}
    </FinanceRootLayout>
  );
};
